import { type FormEvent, type JSX, type ReactNode, useEffect, useId, useRef, useState } from 'react';

import { routes } from '../../routes';
import {
  SessionApiError,
  completeMfa,
  requestSignInCode,
  signInWithCode,
  signInWithPassword,
} from '../../session/client';
import { useSession } from '../../session/SessionProvider';
import {
  type CreatedApplication,
  type ProviderApplication,
  createPrefilledApplication,
  liveApplication,
  myApplications,
  requestPasswordLink,
} from './partner-api';
import {
  APPLICANT_TO_FINISH,
  APPLICATION_STATUS_LABEL,
  BUSINESS_TYPES,
  EMPTY_FORM,
  type FormErrors,
  NO_TYPE_REGULATOR,
  PLAN_LABEL,
  type PartnerForm,
  type PlanId,
  ROLE_OPTIONS,
  SITE_OPTIONS,
  businessType,
  toApplication,
  validateStepOne,
  validateStepTwo,
} from './partner-model';
import './partner.css';

/**
 * Partner with us — `Just Treated Partner With Us.dc.html` (the newer frame;
 * the v1 frame is the older one-card form).
 *
 * ## What is real
 *
 * The form starts a real application in the platform's provider onboarding —
 * the same journey the partner portal runs — and prefills it with what was
 * typed here. The reference shown is the platform's. A visitor who is not
 * signed in confirms their work email with a code inline (or uses their
 * password), exactly as on the Log In page; a signed-in person with an
 * application sees its status and a way into the partner portal instead.
 *
 * ## What the frame draws that is not here, and why (D5, D11)
 *
 * No claim is drawn that the platform cannot back:
 *
 * - the testimonials, the partner logo wall and the film are gone (no such
 *   quotes, partners or film exist);
 * - "Thousands of people … every day", "Reach thousands of local patients",
 *   "live in 10 working days", "within 2 working days", "weekly payouts",
 *   "Popular choice", "reduced commission", "DBS-checked couriers", "Free
 *   photography and storefront set-up" and the 0330 phone line are cut;
 * - "Wholesaler savings with Penny" is marked Coming soon — procurement is
 *   built but switched off;
 * - "We've sent a confirmation to …" is cut: no confirmation email is sent;
 * - "By applying you accept our Partner Terms and Privacy Notice" is cut:
 *   neither document is published yet;
 * - the plans carry no price: the platform defines none.
 */

const PARTNER_PORTAL = '/partners/';

type CardState =
  | { readonly kind: 'form' }
  | { readonly kind: 'verify' }
  | { readonly kind: 'working' }
  | { readonly kind: 'done'; readonly created: CreatedApplication; readonly newAccount: boolean }
  | { readonly kind: 'existing'; readonly application: ProviderApplication; readonly justSignedIn: boolean };

// -- Small pieces -----------------------------------------------------------

function Arrow(): JSX.Element {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function Icon({ d, size = 22 }: { readonly d: string; readonly size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

function Field({
  label,
  error,
  help,
  children,
  wide,
}: {
  readonly label: string;
  readonly error?: string | undefined;
  readonly help?: string;
  readonly children: (id: string, describedBy: string | undefined) => ReactNode;
  readonly wide?: boolean;
}): JSX.Element {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const describedBy = [error !== undefined ? errorId : undefined, help !== undefined ? helpId : undefined]
    .filter((part): part is string => part !== undefined)
    .join(' ');
  return (
    <div className={`jt-pw-field${wide === true ? ' jt-pw-field--wide' : ''}`}>
      <label className="jt-pw-field__label" htmlFor={id}>
        {label}
      </label>
      {children(id, describedBy === '' ? undefined : describedBy)}
      {error !== undefined ? (
        <span id={errorId} className="jt-pw-field__error">
          {error}
        </span>
      ) : null}
      {help !== undefined ? (
        <span id={helpId} className="jt-pw-field__help">
          {help}
        </span>
      ) : null}
    </div>
  );
}

function messageFor(error: unknown, context: 'request' | 'code' | 'password' | 'mfa' | 'create'): string {
  if (!(error instanceof SessionApiError)) return 'Something went wrong. Try again.';
  switch (error.code) {
    case 'AUTH_RATE_LIMITED':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'AUTH_CHALLENGE_INVALID':
      return "That code isn't right, or it has expired. Check your email, or resend a new code.";
    case 'AUTH_INVALID_CREDENTIALS':
      return "That email and password don't match. Check them, or use an email code instead.";
    case 'AUTH_VERIFICATION_REQUIRED':
      return 'This email address has not been verified yet. Use an email code instead — it verifies it.';
    case 'AUTH_MFA_INVALID':
      return "That code isn't right. Check your authenticator app and try again.";
    case 'VALIDATION_FAILED':
      return context === 'code' || context === 'mfa' ? 'Enter the 6-digit code' : error.message;
    default:
      return error.message;
  }
}

function shortDate(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// -- The inline sign-in -----------------------------------------------------

type VerifyStep = 'code' | 'password' | 'mfa';

/**
 * The Log In page's code and password steps, inside the card.
 *
 * The code goes to the work email the visitor just typed: confirming it is
 * what makes the application theirs. "Use a password instead" is there for an
 * existing account that signs in with one (D4).
 */
function InlineSignIn({
  email,
  onSignedIn,
  onBack,
}: {
  readonly email: string;
  readonly onSignedIn: (created: boolean) => void;
  readonly onBack: () => void;
}): JSX.Element {
  const [step, setStep] = useState<VerifyStep>('code');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [cool, setCool] = useState(0);
  const requested = useRef(false);
  const ids = useId();

  useEffect(() => {
    if (cool <= 0) return undefined;
    const timer = window.setTimeout(() => setCool((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearTimeout(timer);
  }, [cool]);

  const send = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await requestSignInCode(email);
      setSent(true);
      setCool(30);
    } catch (caught) {
      setError(messageFor(caught, 'request'));
    } finally {
      setBusy(false);
    }
  };

  // The code is asked for as soon as the visitor presses Start application.
  useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    void send();
  }, []);

  const verify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError('Enter the 6-digit code from your email');
    setBusy(true);
    setError('');
    try {
      const session = await signInWithCode(email, code);
      onSignedIn(session.created);
    } catch (caught) {
      setBusy(false);
      if (caught instanceof SessionApiError && caught.code === 'AUTH_PASSWORD_SIGN_IN_REQUIRED') {
        setNotice(caught.message);
        setStep('password');
        return;
      }
      setError(messageFor(caught, 'code'));
    }
  };

  const passwordSignIn = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (password === '') return setError('Enter your password');
    setBusy(true);
    setError('');
    try {
      await signInWithPassword(email, password);
      setPassword('');
      onSignedIn(false);
    } catch (caught) {
      setBusy(false);
      if (caught instanceof SessionApiError && caught.code === 'AUTH_MFA_REQUIRED') {
        const token = caught.details.mfaToken;
        setMfaToken(typeof token === 'string' ? token : undefined);
        setPassword('');
        setCode('');
        setStep('mfa');
        return;
      }
      setError(messageFor(caught, 'password'));
    }
  };

  const verifyMfa = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError('Enter the 6-digit code from your authenticator app');
    if (mfaToken === undefined) {
      setStep('password');
      return setError('Log in with your password again.');
    }
    setBusy(true);
    setError('');
    try {
      await completeMfa(mfaToken, code);
      onSignedIn(false);
    } catch (caught) {
      setBusy(false);
      setError(messageFor(caught, 'mfa'));
    }
  };

  const errorLine =
    error !== '' ? (
      <span className="jt-pw-field__error" role="alert">
        {error}
      </span>
    ) : null;

  if (step === 'password') {
    return (
      <form className="jt-pw-verify" onSubmit={(event) => void passwordSignIn(event)} noValidate>
        <h3 className="jt-pw-verify__title">Log in to continue</h3>
        {notice !== '' ? <p className="jt-pw-verify__copy">{notice}</p> : null}
        <p className="jt-pw-verify__copy">
          Log in as <b>{email}</b> and we&apos;ll start your application.
        </p>
        <div className="jt-pw-field jt-pw-field--wide">
          <label className="jt-pw-field__label" htmlFor={`${ids}-pw`}>
            Password
          </label>
          <input
            id={`${ids}-pw`}
            className="jt-pw-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={error !== ''}
          />
          {errorLine}
        </div>
        <button type="submit" className="jt-pw-primary" disabled={busy}>
          {busy ? 'Logging in…' : 'Log in and start application'}
        </button>
        <p className="jt-pw-verify__switch">
          <button
            type="button"
            className="jt-pw-link"
            onClick={() => {
              setError('');
              setNotice('');
              setCode('');
              setStep('code');
            }}
          >
            Use an email code instead
          </button>
          <button type="button" className="jt-pw-link" onClick={onBack}>
            Back to your details
          </button>
        </p>
      </form>
    );
  }

  if (step === 'mfa') {
    return (
      <form className="jt-pw-verify" onSubmit={(event) => void verifyMfa(event)} noValidate>
        <h3 className="jt-pw-verify__title">Enter your authenticator code</h3>
        <div className="jt-pw-field jt-pw-field--wide">
          <label className="jt-pw-field__label" htmlFor={`${ids}-mfa`}>
            Authenticator code
          </label>
          <input
            id={`${ids}-mfa`}
            className="jt-pw-input jt-pw-input--code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            aria-invalid={error !== ''}
          />
          {errorLine}
        </div>
        <button type="submit" className="jt-pw-primary" disabled={busy}>
          {busy ? 'Checking…' : 'Verify and start application'}
        </button>
      </form>
    );
  }

  return (
    <form className="jt-pw-verify" onSubmit={(event) => void verify(event)} noValidate>
      <h3 className="jt-pw-verify__title">Confirm your work email</h3>
      <p className="jt-pw-verify__copy">
        {sent ? (
          <>
            We&apos;ve emailed a 6-digit code to <b>{email}</b>. Enter it to start your application.
          </>
        ) : busy ? (
          <>Sending a code to {email}…</>
        ) : (
          <>
            We&apos;ll email a 6-digit code to <b>{email}</b>.
          </>
        )}
      </p>
      <div className="jt-pw-field jt-pw-field--wide">
        <label className="jt-pw-field__label" htmlFor={`${ids}-code`}>
          Verification code
        </label>
        <input
          id={`${ids}-code`}
          className="jt-pw-input jt-pw-input--code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
          aria-invalid={error !== ''}
        />
        {errorLine}
      </div>
      <button type="submit" className="jt-pw-primary" disabled={busy || !sent}>
        {busy && sent ? 'Checking…' : 'Verify and start application'}
      </button>
      <p className="jt-pw-verify__switch">
        <button type="button" className="jt-pw-link" disabled={busy || cool > 0} onClick={() => void send()}>
          {cool > 0 ? `Resend code in ${cool}s` : sent ? 'Resend code' : 'Send code'}
        </button>
        <button
          type="button"
          className="jt-pw-link"
          onClick={() => {
            setError('');
            setStep('password');
          }}
        >
          Use a password instead
        </button>
        <button type="button" className="jt-pw-link" onClick={onBack}>
          Back to your details
        </button>
      </p>
    </form>
  );
}

// -- The application card ----------------------------------------------------

function Select({
  id,
  describedBy,
  value,
  invalid,
  options,
  onChange,
}: {
  readonly id: string;
  readonly describedBy: string | undefined;
  readonly value: string;
  readonly invalid: boolean;
  readonly options: readonly { readonly id: string; readonly label: string }[];
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <span className="jt-pw-select">
      <select
        id={id}
        className={`jt-pw-input${value === '' ? ' jt-pw-input--empty' : ''}`}
        value={value}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select one</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
      <svg
        className="jt-pw-select__chev"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#5b2ede"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </span>
  );
}

function ExistingApplication({
  application,
  justSignedIn,
}: {
  readonly application: ProviderApplication;
  readonly justSignedIn: boolean;
}): JSX.Element {
  const label = APPLICATION_STATUS_LABEL[application.status] ?? application.status;
  const toFinish = APPLICANT_TO_FINISH.has(application.status);
  return (
    <div className="jt-pw-done" aria-live="polite">
      <h2 className="jt-pw-card__title">Your partner application</h2>
      {justSignedIn ? (
        <p className="jt-pw-done__copy">
          You already have an application with us, so we haven&apos;t started another.
        </p>
      ) : null}
      <dl className="jt-pw-status">
        <div>
          <dt>Reference</dt>
          <dd>
            <b>{application.reference}</b>
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <span className="jt-pw-pill">{label}</span>
          </dd>
        </div>
        <div>
          <dt>Started</dt>
          <dd>{shortDate(application.createdAt)}</dd>
        </div>
        {application.submittedAt !== undefined ? (
          <div>
            <dt>Submitted</dt>
            <dd>{shortDate(application.submittedAt)}</dd>
          </div>
        ) : null}
      </dl>
      <p className="jt-pw-done__copy">
        {toFinish
          ? 'Finish the remaining sections and submit it in the partner portal.'
          : 'Follow it, and answer anything we ask, in the partner portal.'}
      </p>
      <a className="jt-pw-primary" href={PARTNER_PORTAL}>
        Continue in the partner portal
        <Arrow />
      </a>
    </div>
  );
}

function Received({
  form,
  created,
  newAccount,
}: {
  readonly form: PartnerForm;
  readonly created: CreatedApplication;
  readonly newAccount: boolean;
}): JSX.Element {
  const type = businessType(form.type);
  const [link, setLink] = useState<'idle' | 'busy' | 'sent' | 'failed'>('idle');
  const steps = [
    { i: '✓', t: 'Application received', s: 'Just now', done: true },
    {
      i: '2',
      t: 'Finish it in the partner portal',
      s: 'What you told us here is already filled in. Add the rest, then submit it.',
      done: false,
    },
    {
      i: '3',
      t: 'We verify your registration',
      s:
        type !== undefined && !type.regulator.optional
          ? `We check your ${type.regulator.label} before you go live.`
          : 'We check your business before you go live.',
      done: false,
    },
  ];
  return (
    <div className="jt-pw-done" aria-live="polite">
      <span className="jt-pw-done__tick" aria-hidden="true">
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
      </span>
      <h2 className="jt-pw-card__title">Application received, {form.first.trim()}</h2>
      <p className="jt-pw-done__copy">
        Your reference is <b data-testid="partner-reference">{created.application.reference}</b>.
      </p>
      <ol className="jt-pw-next">
        {steps.map((step) => (
          <li key={step.t} className="jt-pw-next__row">
            <span className={`jt-pw-next__dot${step.done ? ' jt-pw-next__dot--done' : ''}`}>{step.i}</span>
            <span className="jt-pw-next__text">
              <span className="jt-pw-next__t">{step.t}</span>
              <span className="jt-pw-next__s">{step.s}</span>
            </span>
          </li>
        ))}
      </ol>
      {created.unsaved.length > 0 ? (
        <p className="jt-pw-done__warn" role="status">
          Some of your details didn&apos;t save. You can add them in the partner portal.
        </p>
      ) : null}
      <a className="jt-pw-primary" href={PARTNER_PORTAL}>
        Continue in the partner portal
        <Arrow />
      </a>
      {newAccount ? (
        <div className="jt-pw-done__password">
          <p className="jt-pw-done__copy">
            The partner portal logs in with a password, and your new account doesn&apos;t have one yet.
          </p>
          {link === 'sent' ? (
            <p className="jt-pw-done__copy" role="status">
              We&apos;ve emailed a link to set your password to <b>{form.email.trim()}</b>.
            </p>
          ) : (
            <button
              type="button"
              className="jt-pw-secondary"
              disabled={link === 'busy'}
              onClick={() => {
                setLink('busy');
                requestPasswordLink(form.email.trim())
                  .then(() => setLink('sent'))
                  .catch(() => setLink('failed'));
              }}
            >
              {link === 'busy' ? 'Sending…' : 'Email me a link to set a password'}
            </button>
          )}
          {link === 'failed' ? (
            <span className="jt-pw-field__error" role="alert">
              We couldn&apos;t send that just now. Try again.
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ApplyCard({
  plan,
  onClearPlan,
}: {
  readonly plan: PlanId | undefined;
  readonly onClearPlan: () => void;
}): JSX.Element {
  const session = useSession();
  const [form, setForm] = useState<PartnerForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [step, setStep] = useState<1 | 2>(1);
  const [state, setState] = useState<CardState>({ kind: 'form' });
  const [error, setError] = useState('');
  const [checked, setChecked] = useState(false);
  const signedIn = session.status === 'signedIn' && session.user !== undefined;
  const type = businessType(form.type);

  // A signed-in person with an application sees it, not a second form.
  useEffect(() => {
    if (!signedIn || checked) return;
    setChecked(true);
    myApplications()
      .then((applications) => {
        const live = liveApplication(applications);
        if (live !== undefined) setState({ kind: 'existing', application: live, justSignedIn: false });
      })
      .catch(() => undefined);
  }, [signedIn, checked]);

  // Their work email starts as the address they log in with; they can change it.
  useEffect(() => {
    if (signedIn && session.user !== undefined && form.email === '') {
      setForm((previous) => ({ ...previous, email: session.user?.email ?? '' }));
    }
  }, [signedIn]);

  const set =
    (key: keyof PartnerForm) =>
    (value: string): void => {
      setForm((previous) => ({ ...previous, [key]: value }));
      setErrors((previous) => ({ ...previous, [key]: undefined }));
    };

  const next = (): void => {
    const found = validateStepOne(form);
    setErrors(found);
    if (Object.keys(found).length === 0) setStep(2);
  };

  const create = async (newAccount: boolean, justSignedIn: boolean): Promise<void> => {
    setState({ kind: 'working' });
    setError('');
    try {
      const live = liveApplication(await myApplications());
      if (live !== undefined) {
        setState({ kind: 'existing', application: live, justSignedIn });
        return;
      }
      const created = await createPrefilledApplication(toApplication(form, plan));
      setState({ kind: 'done', created, newAccount });
    } catch (caught) {
      setError(messageFor(caught, 'create'));
      setState({ kind: 'form' });
    }
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const one = validateStepOne(form);
    if (Object.keys(one).length > 0) {
      setErrors(one);
      setStep(1);
      return;
    }
    const two = validateStepTwo(form);
    setErrors(two);
    if (Object.keys(two).length > 0) return;
    if (signedIn) void create(false, false);
    else {
      // Signing in below checks for an existing application itself; the
      // page-load check must not race it.
      setChecked(true);
      setState({ kind: 'verify' });
    }
  };

  if (state.kind === 'existing') {
    return <ExistingApplication application={state.application} justSignedIn={state.justSignedIn} />;
  }
  if (state.kind === 'done') {
    return <Received form={form} created={state.created} newAccount={state.newAccount} />;
  }

  const regulator = type?.regulator ?? NO_TYPE_REGULATOR;
  const stepTitle =
    state.kind === 'verify' ? 'Confirm it’s you' : step === 1 ? 'Your business' : 'Who we should contact';

  return (
    <>
      <div className="jt-pw-card__head">
        <h2 className="jt-pw-card__title">Grow your patients, orders and bookings</h2>
        {signedIn ? (
          <span className="jt-pw-card__sub">You&apos;re logged in as {session.user?.email}.</span>
        ) : (
          <span className="jt-pw-card__sub">
            Already a partner? <a href={PARTNER_PORTAL}>Log in</a>
          </span>
        )}
      </div>
      {plan !== undefined ? (
        <div className="jt-pw-plan">
          <span>Plan: {PLAN_LABEL[plan]}</span>
          <button type="button" className="jt-pw-link" onClick={onClearPlan}>
            Change
          </button>
        </div>
      ) : null}
      <div className="jt-pw-progress">
        <div className="jt-pw-progress__row">
          <span className="jt-pw-progress__title">{stepTitle}</span>
          <span className="jt-pw-progress__n">Step {step} of 2</span>
        </div>
        <div className="jt-pw-progress__bar" aria-hidden="true">
          <span className="jt-pw-progress__seg jt-pw-progress__seg--on" />
          <span className={`jt-pw-progress__seg${step === 2 ? ' jt-pw-progress__seg--on' : ''}`} />
        </div>
      </div>

      {state.kind === 'verify' ? (
        <InlineSignIn
          email={form.email.trim()}
          onSignedIn={(created) => void create(created, true)}
          onBack={() => setState({ kind: 'form' })}
        />
      ) : state.kind === 'working' ? (
        <p className="jt-pw-working" role="status">
          Starting your application…
        </p>
      ) : (
        <form className="jt-pw-form" onSubmit={submit} noValidate>
          {step === 1 ? (
            <>
              <fieldset className="jt-pw-fields" aria-label="Business details">
                <Field label="Business name" error={errors.biz}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      placeholder="e.g. High Street Pharmacy"
                      autoComplete="organization"
                      value={form.biz}
                      aria-invalid={errors.biz !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('biz')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Business type" error={errors.type}>
                  {(id, describedBy) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      value={form.type}
                      invalid={errors.type !== undefined}
                      options={BUSINESS_TYPES}
                      onChange={(value) => {
                        set('type')(value);
                        setErrors((previous) => ({ ...previous, reg: undefined }));
                      }}
                    />
                  )}
                </Field>
                <Field label="Street address" error={errors.addr}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      placeholder="e.g. 14 Market Street"
                      autoComplete="street-address"
                      value={form.addr}
                      aria-invalid={errors.addr !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('addr')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Postcode" error={errors.pc}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      placeholder="NR2 4TE"
                      autoComplete="postal-code"
                      value={form.pc}
                      aria-invalid={errors.pc !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('pc')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="How many branches or sites?" error={errors.sites} wide>
                  {(id, describedBy) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      value={form.sites}
                      invalid={errors.sites !== undefined}
                      options={SITE_OPTIONS}
                      onChange={set('sites')}
                    />
                  )}
                </Field>
                <Field label={regulator.label} error={errors.reg} help={regulator.help} wide>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      placeholder={regulator.placeholder}
                      autoComplete="off"
                      disabled={type === undefined}
                      value={form.reg}
                      aria-invalid={errors.reg !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('reg')(event.target.value)}
                    />
                  )}
                </Field>
              </fieldset>
              <button type="button" className="jt-pw-primary" onClick={next}>
                Continue
                <Arrow />
              </button>
            </>
          ) : (
            <>
              <fieldset className="jt-pw-fields" aria-label="Owner or superintendent">
                <Field label="First name" error={errors.first}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      placeholder="Priya"
                      autoComplete="given-name"
                      value={form.first}
                      aria-invalid={errors.first !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('first')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Last name" error={errors.last}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      placeholder="Shah"
                      autoComplete="family-name"
                      value={form.last}
                      aria-invalid={errors.last !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('last')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Phone number" error={errors.phone}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      type="tel"
                      placeholder="07700 900123"
                      autoComplete="tel"
                      value={form.phone}
                      aria-invalid={errors.phone !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('phone')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Work email" error={errors.email}>
                  {(id, describedBy) => (
                    <input
                      id={id}
                      className="jt-pw-input"
                      type="email"
                      placeholder="name@pharmacy.co.uk"
                      autoComplete="email"
                      value={form.email}
                      aria-invalid={errors.email !== undefined}
                      aria-describedby={describedBy}
                      onChange={(event) => set('email')(event.target.value)}
                    />
                  )}
                </Field>
                <Field label="Your role" error={errors.role} wide>
                  {(id, describedBy) => (
                    <Select
                      id={id}
                      describedBy={describedBy}
                      value={form.role}
                      invalid={errors.role !== undefined}
                      options={ROLE_OPTIONS}
                      onChange={set('role')}
                    />
                  )}
                </Field>
              </fieldset>
              {error !== '' ? (
                <span className="jt-pw-field__error" role="alert">
                  {error}
                </span>
              ) : null}
              <div className="jt-pw-actions">
                <button
                  type="button"
                  className="jt-pw-back"
                  aria-label="Back to step 1"
                  onClick={() => {
                    setErrors({});
                    setStep(1);
                  }}
                >
                  Back
                </button>
                <button type="submit" className="jt-pw-primary">
                  Start application
                  <Arrow />
                </button>
              </div>
              <p className="jt-pw-fine">
                We check every partner&apos;s registration with the right regulator before they go live.
              </p>
            </>
          )}
        </form>
      )}
    </>
  );
}

// -- The page -----------------------------------------------------------------

const WHY = [
  {
    t: 'Fulfil your way',
    d: 'Delivery with your own drivers or ours, click & collect, or in-person appointments.',
    img: '/home/grow/delivery-courier.webp',
  },
  {
    t: 'Reach more local patients',
    d: 'People come to Just Treated to find medicines, vaccinations and health checks near them.',
    img: '/home/solutions/pharmacies.webp',
  },
  {
    t: 'Tools to grow your business',
    d: 'Promotions and insights in your partner portal help you grow margin as well as volume.',
    img: '/home/portal/revenue-trends.webp',
  },
] as const;

const WHO = [
  {
    t: 'Community pharmacies',
    d: 'Medicines, prescriptions and vaccinations, ordered and booked in a few taps.',
    img: '/home/solutions/pharmacies.webp',
  },
  {
    t: 'Clinics & private GPs',
    d: 'Fill appointment books with patients looking for GP visits, blood tests and travel clinics.',
    img: '/home/solutions/clinics-gps.webp',
  },
  {
    t: 'Dentists & opticians',
    d: 'Online booking for check-ups, hygiene appointments and eye tests.',
    img: '/home/solutions/dentists.webp',
  },
  {
    t: 'Beauty & aesthetics',
    d: "Skin consultations and treatments, with prescriber oversight where it's required.",
    img: '/home/solutions/beauty-aesthetics.webp',
  },
  {
    t: 'Physiotherapy',
    d: 'Assessments, follow-ups and video sessions, booked online.',
    img: '/home/solutions/physiotherapy.webp',
  },
  {
    t: 'Health & wellness stores',
    d: 'Vitamins, nutrition and natural health, ordered online.',
    img: '/home/solutions/health-wellness-brands.webp',
  },
] as const;

const GETS: readonly { line: string; icon: string; soon?: true }[] = [
  { line: 'Self-service partner portal', icon: 'M3.5 5h17v11h-17zM8 20h8M12 16v4' },
  { line: 'Insights on your orders and bookings', icon: 'M4 20V10M9.5 20V4M15 20v-7M20.5 20V8M3 20.5h18' },
  { line: 'Custom and pre-set promotions', icon: 'M3.5 12.6V4.5h8.1l8.9 8.9-8.1 8.1ZM8 8.5h.01' },
  {
    line: 'Wholesaler savings with Penny',
    icon: 'M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9Z',
    soon: true,
  },
  {
    line: 'Your own storefront for medicines, services and bookings',
    icon: 'M4 9.5 5.5 4h13L20 9.5M4 9.5V20h16V9.5M4 9.5h16M9.5 20v-5h5v5',
  },
];

const PLANS: readonly {
  id: PlanId;
  t: string;
  cta: string;
  d: string;
  pts: readonly { t: string; icon: string }[];
}[] = [
  {
    id: 'own',
    t: 'You deliver',
    cta: 'Get started - you deliver',
    d: 'Patients order from your pharmacy or clinic on Just Treated, and you deliver using your own drivers. You control handover and ID checks.',
    pts: [
      {
        t: 'Manage your own drivers',
        icon: 'M5 8.5V6a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v2.5M3.5 8.5h17v9a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1ZM9 13h6M12 10v6',
      },
      {
        t: 'Your delivery area, your hours',
        icon: 'M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21ZM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z',
      },
    ],
  },
  {
    id: 'jt',
    t: 'Just Treated delivers',
    cta: 'Get started - we deliver',
    d: "When a patient orders from you, a Just Treated courier collects from your counter. You dispense and bag the order, and we'll take care of the rest.",
    pts: [
      {
        t: 'Access to our courier network',
        icon: 'M3.5 15.5V9h9v6.5M12.5 11.5h4l3 3v1h-7M7 18.5a1.8 1.8 0 1 0 0-.01M17 18.5a1.8 1.8 0 1 0 0-.01',
      },
      {
        t: 'Hand over each order with a code',
        icon: 'M7 3.5h10a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-15a1 1 0 0 1 1-1ZM9.5 12l1.8 1.8 3.4-3.6',
      },
    ],
  },
];

const STEPS = [
  {
    t: 'Apply',
    d: 'Tell us about your business and give us your GPhC or CQC registration.',
    img: '/home/solutions/pharmacies.webp',
    icon: 'M5 4h10l4 4v12H5zM14 4v5h5M8.5 13h7M8.5 16.5h5',
  },
  {
    t: 'Verify & set up',
    d: 'We check your registration, and you finish your application and storefront in the partner portal.',
    img: '/home/portal/latest-sales.webp',
    icon: 'M12 3.2 5 6v5.4c0 4.4 3 8 7 9.4 4-1.4 7-5 7-9.4V6ZM9 12l2.2 2.2L15.4 10',
  },
  {
    t: 'Start serving patients',
    d: 'Once you are approved, receive your first orders and bookings.',
    img: '/home/grow/clinician.webp',
    icon: 'M3.5 17 9 11.5l4 4 7.5-8M15 7.5h5.5V13',
  },
] as const;

/** Only answers the platform backs. The frame's other claims are listed at the top of this file. */
export const PARTNER_FAQ: readonly (readonly [string, string])[] = [
  [
    'How much commission will I pay?',
    'Rates are not published on this page. They are agreed with you during set-up, before you take your first order. Starting an application costs nothing.',
  ],
  [
    'Do I need to be registered with a regulator?',
    'Yes, where your services are regulated. Pharmacies must be on the GPhC register, and clinics, GPs and dental practices registered with the CQC. We check registrations before you go live.',
  ],
  [
    'Can I offer prescription-only medicines?',
    'Yes, through our online consultation flow, where a prescriber reviews each request before anything is supplied. What you can offer depends on what your application is approved for.',
  ],
  [
    "I don't have any drivers. Can I still join?",
    'Yes. Choose Just Treated delivers and our couriers collect from your counter, or offer collection and appointments only.',
  ],
  [
    'How long does it take to go live?',
    'It depends on how quickly your registration and documents can be checked. You can follow every step of your application in the partner portal.',
  ],
  [
    'Can I list multiple branches?',
    'Yes. Add each branch, with its own premises number, in the partner portal, and manage them all from one partner account.',
  ],
  [
    'How do I get paid?',
    'Payouts go to the business bank account you add in the partner portal, with a statement of orders, refunds and fees.',
  ],
];

export function PartnerPage(): JSX.Element {
  const [plan, setPlan] = useState<PlanId | undefined>(undefined);
  const [who, setWho] = useState(0);
  const [stepOn, setStepOn] = useState(0);
  const [faq, setFaq] = useState<number | undefined>(0);
  const applyRef = useRef<HTMLDivElement>(null);
  const plansRef = useRef<HTMLElement>(null);

  const toForm = (): void => {
    const element = applyRef.current;
    if (element === null) return;
    window.scrollTo({ top: element.getBoundingClientRect().top + window.scrollY - 92, behavior: 'smooth' });
    element.querySelector<HTMLElement>('input, select')?.focus({ preventScroll: true });
  };

  return (
    <div className="jt-pw">
      <section className="jt-pw-hero">
        <div
          className="jt-pw-hero__band"
          role="img"
          aria-label="A pharmacist at the counter of a modern pharmacy"
        >
          <span className="jt-pw-hero__fade" aria-hidden="true" />
        </div>
        <div className="jt-pw-wrap jt-pw-hero__grid">
          <div className="jt-pw-hero__text">
            <h1 className="jt-pw-hero__title">
              <span className="jt-pw-hero__light">Grow your practice</span>
              <span className="jt-pw-hero__bold">beyond your front door</span>
            </h1>
          </div>
          <div id="apply" ref={applyRef} className="jt-pw-card">
            <ApplyCard
              plan={plan}
              onClearPlan={() => {
                setPlan(undefined);
                plansRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            />
          </div>
        </div>
      </section>

      <section className="jt-pw-section">
        <div className="jt-pw-wrap jt-pw-stack">
          <h2 className="jt-pw-h2">Why Just Treated?</h2>
          <div className="jt-pw-why">
            {WHY.map((item) => (
              <article key={item.t} className="jt-pw-why__item">
                <img className="jt-pw-why__img" src={item.img} alt={item.t} loading="lazy" decoding="async" />
                <h3 className="jt-pw-h3">{item.t}</h3>
                <p className="jt-pw-copy">{item.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="jt-pw-section jt-pw-section--tint">
        <div className="jt-pw-wrap jt-pw-stack">
          <h2 className="jt-pw-h2">Who we work with</h2>
          <div className="jt-pw-who">
            {[0, 1, 2].map((offset) => {
              const item = WHO[(who * 3 + offset) % WHO.length];
              if (item === undefined) return null;
              return (
                <article key={item.t} className="jt-pw-who__card">
                  <img
                    className="jt-pw-who__img"
                    src={item.img}
                    alt={item.t}
                    loading="lazy"
                    decoding="async"
                  />
                  <h3 className="jt-pw-who__t">{item.t}</h3>
                  <p className="jt-pw-who__d">{item.d}</p>
                </article>
              );
            })}
          </div>
          <div className="jt-pw-pager">
            <button
              type="button"
              className="jt-pw-pager__arrow"
              aria-label="Previous"
              onClick={() => setWho((who + 1) % 2)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#5b2ede"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M14.6 5.4 8 12l6.6 6.6" />
              </svg>
            </button>
            <span className="jt-pw-pager__dots">
              {[0, 1].map((page) => (
                <button
                  key={page}
                  type="button"
                  aria-label={`Page ${page + 1}`}
                  aria-current={who === page}
                  className={`jt-pw-pager__dot${who === page ? ' jt-pw-pager__dot--on' : ''}`}
                  onClick={() => setWho(page)}
                />
              ))}
            </span>
            <button
              type="button"
              className="jt-pw-pager__arrow"
              aria-label="Next"
              onClick={() => setWho((who + 1) % 2)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#5b2ede"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M9.4 5.4 16 12l-6.6 6.6" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section className="jt-pw-section">
        <div className="jt-pw-wrap jt-pw-get">
          <div className="jt-pw-stack jt-pw-stack--tight">
            <h2 className="jt-pw-h2">What you&apos;ll get with us</h2>
            <ul className="jt-pw-get__list">
              {GETS.map((item) => (
                <li key={item.line} className="jt-pw-get__item">
                  <Icon d={item.icon} />
                  <span>{item.line}</span>
                  {item.soon === true ? <span className="jt-pw-soon">Coming soon</span> : null}
                </li>
              ))}
            </ul>
            <button type="button" className="jt-pw-dark" onClick={toForm}>
              Get started
            </button>
          </div>
          <div className="jt-pw-get__media">
            <span className="jt-pw-get__frame" aria-hidden="true" />
            <img
              className="jt-pw-get__img"
              src="/home/portal/promotions.webp"
              alt="The partner portal's promotions"
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="jt-pw-section jt-pw-section--tint" id="plans" ref={plansRef}>
        <div className="jt-pw-wrap jt-pw-stack">
          <h2 className="jt-pw-h2">The right option for your business</h2>
          <div className="jt-pw-plans">
            {PLANS.map((option) => (
              <article
                key={option.id}
                className={`jt-pw-planc${plan === option.id ? ' jt-pw-planc--on' : ''}`}
              >
                <div className="jt-pw-planc__head">
                  <h3 className="jt-pw-planc__t">{option.t}</h3>
                </div>
                <div className="jt-pw-planc__body">
                  <p className="jt-pw-copy">{option.d}</p>
                  <div className="jt-pw-planc__rule" />
                  <h4 className="jt-pw-planc__k">Benefits you&apos;ll love</h4>
                  <ul className="jt-pw-planc__pts">
                    {option.pts.map((point) => (
                      <li key={point.t}>
                        <Icon d={point.icon} size={20} />
                        {point.t}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className="jt-pw-dark jt-pw-dark--block"
                    onClick={() => {
                      setPlan(option.id);
                      toForm();
                    }}
                  >
                    {option.cta}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="jt-pw-section">
        <div className="jt-pw-wrap jt-pw-steps">
          <div className="jt-pw-steps__media">
            <img
              className="jt-pw-steps__img"
              src={STEPS[stepOn]?.img}
              alt={STEPS[stepOn]?.t ?? ''}
              loading="lazy"
              decoding="async"
            />
            <span className="jt-pw-steps__badge">
              <span className="jt-pw-steps__num">{stepOn + 1}</span>
              <span className="jt-pw-steps__bt">
                <span className="jt-pw-steps__of">Step {stepOn + 1} of 3</span>
                <span>{STEPS[stepOn]?.t}</span>
              </span>
            </span>
          </div>
          <div className="jt-pw-stack jt-pw-stack--tight">
            <h2 className="jt-pw-h2">How to get started on Just Treated</h2>
            <p className="jt-pw-copy">Three steps from application to your first patient.</p>
            <ol className="jt-pw-steps__list">
              {STEPS.map((item, index) => (
                <li key={item.t}>
                  <button
                    type="button"
                    className={`jt-pw-steps__row${stepOn === index ? ' jt-pw-steps__row--on' : ''}`}
                    aria-pressed={stepOn === index}
                    onClick={() => setStepOn(index)}
                  >
                    <span className="jt-pw-steps__icon">
                      <Icon d={item.icon} size={16} />
                    </span>
                    <span className="jt-pw-steps__text">
                      <span className="jt-pw-steps__t">
                        {index + 1}. {item.t}
                      </span>
                      <span className="jt-pw-steps__d">{item.d}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
            <button type="button" className="jt-pw-dark" onClick={toForm}>
              Start your application
              <Arrow />
            </button>
          </div>
        </div>
      </section>

      <section className="jt-pw-section">
        <div className="jt-pw-wrap jt-pw-stack">
          <h2 className="jt-pw-h2">Still have questions?</h2>
          <div className="jt-pw-faq">
            {PARTNER_FAQ.map(([question, answer], index) => {
              const open = faq === index;
              return (
                <div key={question} className="jt-pw-faq__item">
                  <h3 className="jt-pw-faq__h">
                    <button
                      type="button"
                      className="jt-pw-faq__q"
                      aria-expanded={open}
                      onClick={() => setFaq(open ? undefined : index)}
                    >
                      {question}
                      <svg
                        className={`jt-pw-faq__chev${open ? ' jt-pw-faq__chev--open' : ''}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                  </h3>
                  {open ? <p className="jt-pw-faq__a">{answer}</p> : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="jt-pw-end">
        <div className="jt-pw-end__band" role="img" aria-label="Partners planning together" />
        <div className="jt-pw-end__inner">
          <h2 className="jt-pw-end__title">
            <span className="jt-pw-hero__light">The missing piece</span>
            <span className="jt-pw-hero__bold">in growing your practice</span>
          </h2>
          <button type="button" className="jt-pw-primary jt-pw-primary--pill" onClick={toForm}>
            Join Just Treated
          </button>
        </div>
      </section>
    </div>
  );
}

/** For tests: where the page sends a partner. */
export const PARTNER_PORTAL_PATH = PARTNER_PORTAL;
export const PARTNER_ROUTE = routes.partnersWithUs.path;
